import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "@/data/site";

const CATEGORIES = ["All", "Web Development", "Mobile App", "E-commerce", "Blockchain", "FinTech", "Logistics"];

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const items = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="portfolio-page">
      <section className="border-b border-[#1f1f22] relative overflow-hidden">
        <div className="glow-spot" style={{ width: 700, height: 600, background: "#DC1F33", top: "-200px", right: "-160px" }} />
        <div className="container-x py-24 md:py-32 relative">
          <div className="label-mono mb-6">// Portfolio / Selected Work</div>
          <h1 className="font-display text-[clamp(40px,7vw,96px)] font-black uppercase tracking-tight leading-[0.92] text-white max-w-5xl">
            Real products.<br /><span className="text-[#DC1F33]">Real numbers.</span><br /><span className="text-zinc-500">Real businesses worldwide.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl">
            A glimpse at the platforms, apps and infrastructure we've engineered for ambitious teams across the globe.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-[#1f1f22] sticky top-[72px] bg-[#09090B]/90 backdrop-blur z-30">
        <div className="container-x py-5 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`portfolio-filter-${c.toLowerCase().replace(/\W+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 border text-sm font-display tracking-tight transition-colors ${
                filter === c
                  ? "border-[#DC1F33] bg-[#DC1F33] text-white font-bold"
                  : "border-[#27272A] text-zinc-300 hover:border-[#DC1F33] hover:text-[#DC1F33]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="border-b border-[#1f1f22]">
        <div className="container-x py-16">
          {items.length === 0 ? (
            <div className="text-center text-zinc-500 py-32 font-display tracking-tight">
              No projects in this category yet. <Link to="/contact" className="text-[#DC1F33]">Be our first.</Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1f1f22] border border-[#1f1f22]">
              {items.map((p) => (
                <div
                  key={p.slug}
                  data-testid={`portfolio-card-${p.slug}`}
                  className="bg-[#0B0B0D] p-1 card-hover group"
                >
                  <div className="aspect-[4/3] overflow-hidden border border-[#1f1f22]">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="label-mono">{p.category}</div>
                      <ArrowUpRight size={16} className="text-zinc-500 group-hover:text-[#DC1F33] transition-colors" />
                    </div>
                    <div className="font-display text-xl font-bold text-white mb-2 tracking-tight">{p.title}</div>
                    <div className="text-sm text-zinc-400 mb-6 leading-relaxed">{p.blurb}</div>
                    <div className="flex items-center justify-between border-t border-[#1f1f22] pt-4">
                      <span className="text-[#DC1F33] font-display font-bold text-sm">{p.metric}</span>
                      <div className="flex gap-2">
                        {p.stack.map((s) => (
                          <span key={s} className="label-mono">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="border border-[#27272A] p-12 md:p-16 bg-[#0E0E10] flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <div className="label-mono mb-3">// Be next</div>
              <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight">Want to be our next case study?</h3>
              <p className="text-zinc-400 mt-3 max-w-xl">Tell us about your project — we'll send a free architecture recommendation and rough timeline.</p>
            </div>
            <Link to="/contact" data-testid="portfolio-final-cta" className="btn-primary">
              Start a Project <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
